/**
 * PTL Delivery Health (merchant, read-only): filters, labels and API helpers shared by its pages.
 * KPI rules follow the Admin Panel; see docs/PTL_PLAN.md §3.
 */
import { addDays, subDays } from 'date-fns';
import { formatDate } from '../../helpers/UtilityHelper';
import { WAITING_FIRST_APPOINTMENT_FILTER } from '../appointments/shared/constants';
import { ERROR, WARNING, PRIMARY_BLUE, PURPLE, SUCCESS, ORANGE } from '../../styles/foundation/colors';

export const PTL_BASE_PATH = '/ptl-delivery-health';
/** The section's URL before it was renamed from PTL Visibility; old links redirect (LegacyPathRedirect). */
export const PTL_LEGACY_BASE_PATH = '/ptl-visibility';
export const PTL_PATHS = {
  today: PTL_BASE_PATH,
  nonAbdToday: `${PTL_BASE_PATH}/non-abd-today`,
  // Moved out of PTL Delivery Health (review feedback): Overdue under ABD PTL Shipments, EDD Breaches under
  // Non-ABD PTL Shipments, Performance Overview at the top of PTL Dashboard. The old paths redirect.
  overdue: '/appointments/overdue',
  eddBreaches: '/nonabd-edd-breaches',
  pod: `${PTL_BASE_PATH}/pod-analytics`,
  performance: '/ptl-dashboard/performance-overview',
  // The existing All ABD Shipments page, in its "scheduled today" mode.
  abdToday: '/appointments/all?scheduled=today',
  // The existing All Non-ABD Shipments page; "Scheduled today" opens it with an EDD-today filter (router state).
  nonAbdAll: '/b2b-orders',
};

const dayStart = (d) => `Date('${formatDate(d, 'yyyy-MM-dd')} 00:00:00')`;

/** `field` falls on the calendar day of `now` (00:00 up to, not including, the next 00:00). */
export const onDayFilter = (field, now) => [
  { field, operator: 'gte', value: dayStart(now) },
  { field, operator: 'lt', value: dayStart(addDays(now, 1)) },
];

const NOT_CANCELLED = { field: 'orderStatus', operator: 'ne', value: 'CANCELLED_ORDER' };

/** ABD shipments whose current appointment is today (on top of the ABD baseline). */
export const abdTodayFilter = (now) => [
  ...onDayFilter('appointmentDeliveryData.appointmentDeliveryStartTime', now),
  NOT_CANCELLED,
];

/** Non-ABD shipments due today: EDD today (on top of the Non-ABD baseline). */
export const nonAbdTodayFilter = (now) => [...onDayFilter('edd', now), NOT_CANCELLED];

/**
 * Server Date fields (edd, deliveredAt…) come as UTC, e.g. "2026-09-17T18:30:00.000+00:00" = 18 Sep IST.
 * formatDate() drops a "+00:00" suffix and would show the previous day, so pass these through new Date() first.
 * (Appointment slots are LocalDateTime without a zone and are formatted as they are.)
 */
export const fromUtc = (v) => (v ? new Date(v) : null);

export const errorStatus = (e) => e?.status ?? e?.response?.status;

/**
 * True when a request failed because the endpoint is missing or not open to merchants yet: a 403 / 404 / 405,
 * or no response at all while online. The backend sets CORS headers per controller (@CrossOrigin), so a path
 * with no controller yet comes back without them and the browser blocks it as a CORS error (axios: "Network
 * Error", no status).
 */
export const isApiMissing = (e) => {
  const status = errorStatus(e);
  if (status === 403 || status === 404 || status === 405) return true;
  return !!e?.request && !e?.response && navigator.onLine !== false;
};
export const errorText = (e, fallback = 'Something went wrong. Please try again.') =>
  e?.data?.message || e?.response?.data?.message || e?.message || fallback;

// ── Overdue appointments ────────────────────────────────────────
/** Who a missed appointment is attributed to (appointment_closure_category), in merchant wording. */
export const MISS_ATTRIBUTION = {
  PROZO_ATTRIBUTABLE: { label: 'Prozo attributable', color: PRIMARY_BLUE },
  NON_PROZO_ATTRIBUTABLE: { label: 'Client / consignee attributable', color: ORANGE },
  FORCE_MAJEURE: { label: 'Other (force majeure)', color: PURPLE },
  JOINT_ATTRIBUTABLE: { label: 'Joint (Prozo and client)', color: WARNING },
};
export const MISS_ATTRIBUTION_SHORT = {
  PROZO_ATTRIBUTABLE: 'Prozo', NON_PROZO_ATTRIBUTABLE: 'Client', JOINT_ATTRIBUTABLE: 'Joint', FORCE_MAJEURE: 'Force majeure',
};
export const MISS_ATTRIBUTION_KEYS = ['PROZO_ATTRIBUTABLE', 'NON_PROZO_ATTRIBUTABLE', 'JOINT_ATTRIBUTABLE', 'FORCE_MAJEURE'];
/** Shipments whose latest miss carries `category`, so each shipment counts under one owner (Admin Panel rule). */
export const latestMissFilter = (category) => (
  { field: 'appointmentDeliveryHistory.closureCategory', operator: 'lasteq', value: category }
);

/** Admin Panel "Waiting for 1st appt" uses its default AWB date range; the merchant views use the same 90 days. */
export const NO_FIRST_APPOINTMENT_DAYS = 90;
export const noFirstAppointmentFilter = (now) => [
  ...WAITING_FIRST_APPOINTMENT_FILTER,
  { field: 'awbRegisteredDate', operator: 'gte', value: dayStart(subDays(now, NO_FIRST_APPOINTMENT_DAYS - 1)) },
];

/** Order details for an AWB (read-only page, PoD and documents included). */
export const orderDetailPath = (awb) => `/order-detail?waybill=${encodeURIComponent(awb)}`;

// ── EDD breaches ────────────────────────────────────────────────
export const EDD_TABS = [
  { key: 'U', label: 'Undelivered, past EDD' },
  { key: 'D', label: 'Delivered after EDD' },
];

/**
 * Attribution owners as the Admin Panel stores them, with the Admin Panel's colours (pages/ptl-edd-breach/constants.js):
 * color = text / number, bg = chip fill, accent = the tile's left bar. External is shown as "Force majeure" (as in the
 * Non-ABD MIS); hints are in merchant wording (the Admin hints name internal teams).
 */
export const EDD_OWNERS = {
  NONE: { label: 'Not attributed', short: 'Not attributed', hint: 'Prozo is reviewing it', color: '#B72136', bg: '#FFE7D9', accent: '#FF4842', kpiColor: 'red' },
  PROZO: { label: 'Prozo attributable', short: 'Prozo', hint: 'Prozo, its warehouse or carrier', color: '#103996', bg: '#D1E9FC', accent: '#2065D1', kpiColor: 'blue' },
  CLIENT: { label: 'Client attributable', short: 'Client', hint: 'Client or consignee side', color: '#7A4F01', bg: '#FFF7CD', accent: '#FFC107', kpiColor: 'amber' },
  EXTERNAL: { label: 'Force majeure', short: 'Force majeure', hint: 'Rain, strike, curfew and similar', color: '#826AF9', bg: '#F4F6F8', accent: '#3366FF', kpiColor: 'purple' },
};
/** Admin Panel order. */
export const EDD_OWNER_KEYS = ['NONE', 'PROZO', 'CLIENT', 'EXTERNAL'];

export const EDD_BUCKETS = [
  { value: '0-2d', label: '0 to 2 days' },
  { value: '3-5d', label: '3 to 5 days' },
  { value: '6-10d', label: '6 to 10 days' },
  { value: '10d+', label: 'Over 10 days' },
];
export const EDD_BUCKET_LABEL = { '0-2d': '0 to 2 days', '3-5d': '3 to 5 days', '6-10d': '6 to 10 days', '10d+': 'Over 10 days', All: 'All' };

// ── PoD analytics ───────────────────────────────────────────────
/** Server default aging buckets (PoD config agingBucketDays 2/5/7); the summary's bucketLabels win. */
export const POD_FALLBACK_BUCKETS = ['0–2 days', '3–5 days', '6–7 days', 'More than 7 days'];
export const POD_MAX_DAYS = 92;

/** First bucket calm, last bucket red, the ones between warming up (Admin Panel rule). */
export const podBucketColor = (i, count) => {
  if (count <= 1) return ERROR;
  const tones = [SUCCESS, WARNING, ORANGE, ERROR];
  return tones[Math.round((i / (count - 1)) * (tones.length - 1))];
};
